import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
  type OnChangeFn,
  type PaginationState,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { type TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { ChevronRight, Pencil, Trash2, TriangleAlert } from "lucide-react";

import Loading from "@/components/Loading";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { WFPButton } from "@/components/ui/button";
import { getColumnSortAria, SortableColumnHeader } from "@/components/ui/sortable-column-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "@/components/ui/table-pagination";
import { cn } from "@/lib/utils";
import { formatDateTimeCompact } from "@/utils/relative-time";

import { type ComboboxOption } from "../../../InfoHub/Components/Form/Components/MultiSelectCombobox";
import {
  type ExchangeLocation,
  type ExchangeLocationModalType,
  type ExchangeLocationType,
  type UserSummary,
} from "../../types";
import { countLinkedTraders, countTradersForType, EMPTY_VALUE, getAreaLabel } from "../../utils";

interface ListProps {
  areaOptions?: ComboboxOption[];
  errorMessage?: string;
  exchangeLocations?: ExchangeLocation[];
  exchangeLocationTypes?: ExchangeLocationType[];
  isFetching?: boolean;
  onClickDelete: (exchangeLocation: ExchangeLocation) => void;
  onOpenModal: (exchangeLocation: ExchangeLocation, type: ExchangeLocationModalType) => void;
  onSortingChange: OnChangeFn<SortingState>;
  searchTerm?: string;
  sorting: SortingState;
}

interface ExchangeLocationColumnMeta {
  cellClassName?: string;
  headerClassName?: string;
}

type ExchangeLocationColumnDef = ColumnDef<ExchangeLocation, unknown> & {
  meta?: ExchangeLocationColumnMeta;
};

const PAGE_SIZE = 20;
const SYSTEM_USER_LABEL = "System";
const COMPACT_HEADER_CLASS = "px-3 py-2 !whitespace-normal leading-tight";
const NUMERIC_CLASS = "max-w-32 px-3 align-top text-right";

export const DEFAULT_SORTING: SortingState = [{ id: "name", desc: false }];

export const EXCHANGE_LOCATION_SORT_OPTIONS = [
  { id: "name", translationKey: "exchangeLocations.outletLocationName" },
  { id: "location", translationKey: "exchangeLocations.location" },
  { id: "linkedTraders", translationKey: "exchangeLocations.linkedTraders" },
  { id: "active", translationKey: "exchangeLocations.status" },
  { id: "lastUpdated", translationKey: "configuration.list.column.lastUpdated" },
  { id: "created", translationKey: "configuration.list.column.created" },
] as const;

const getColumnMeta = (columnDef: ColumnDef<ExchangeLocation, unknown>) =>
  columnDef.meta as ExchangeLocationColumnMeta | undefined;

const getName = (exchangeLocation?: ExchangeLocation | null) =>
  exchangeLocation?.name?.trim() || "";

const getTimestamp = (value?: string | null) => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();

  return Number.isNaN(timestamp) ? undefined : timestamp;
};

const getFullName = (user?: UserSummary | null) =>
  [user?.first_name, user?.last_name]
    .map(name => name?.trim())
    .filter(Boolean)
    .join(" ") || SYSTEM_USER_LABEL;

const normalizeSearchValue = (value: string) => value.trim().toLocaleLowerCase();

export const filterExchangeLocations = (
  exchangeLocations: ExchangeLocation[],
  areaOptions: ComboboxOption[],
  searchTerm?: string
) => {
  const query = normalizeSearchValue(searchTerm ?? "");

  if (!query) {
    return exchangeLocations;
  }

  return exchangeLocations.filter(exchangeLocation =>
    [getName(exchangeLocation), getAreaLabel(exchangeLocation.location, areaOptions)].some(value =>
      normalizeSearchValue(value).includes(query)
    )
  );
};

export const sortExchangeLocationTypes = (exchangeLocationTypes: ExchangeLocationType[]) =>
  [...exchangeLocationTypes].sort((first, second) =>
    (first.name ?? "").localeCompare(second.name ?? "")
  );

const PlaceholderValue = () => <span className="text-neutral-alpha-300">{EMPTY_VALUE}</span>;

const AuditCell = ({
  dateTime,
  t,
  user,
}: {
  dateTime?: string | null;
  t: TFunction;
  user?: UserSummary | null;
}) => {
  const formattedDateTime = formatDateTimeCompact(dateTime);

  if (!formattedDateTime) {
    return <PlaceholderValue />;
  }

  return (
    <div className="min-w-0 leading-tight">
      <div className="text-foreground">{formattedDateTime}</div>
      <div className="mt-1 truncate text-muted-foreground text-xs">
        {t("configuration.byFullName", { fullName: getFullName(user) })}
      </div>
    </div>
  );
};

const createColumns = ({
  areaOptions,
  exchangeLocationTypes,
  onClickDelete,
  onOpenModal,
  t,
}: {
  areaOptions: ComboboxOption[];
  exchangeLocationTypes: ExchangeLocationType[];
  onClickDelete: (exchangeLocation: ExchangeLocation) => void;
  onOpenModal: (exchangeLocation: ExchangeLocation, type: ExchangeLocationModalType) => void;
  t: TFunction;
}): ExchangeLocationColumnDef[] => [
  {
    id: "actions",
    header: () => (
      <span className="sr-only">
        {t("common.view")} / {t("common.edit")} / {t("common.delete")}
      </span>
    ),
    cell: ({ row }) => (
      <div className="flex items-center justify-start gap-2">
        <WFPButton
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onOpenModal(row.original, "view")}
        >
          {t("common.view")}
          <ChevronRight className="size-4" aria-hidden="true" />
        </WFPButton>
        <WFPButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onOpenModal(row.original, "edit")}
        >
          <Pencil className="size-4" aria-hidden="true" />
          {t("common.edit")}
        </WFPButton>
        <WFPButton
          type="button"
          variant="destructive-secondary"
          size="sm"
          onClick={() => onClickDelete(row.original)}
        >
          <Trash2 className="size-4" aria-hidden="true" />
          {t("common.delete")}
        </WFPButton>
      </div>
    ),
    enableSorting: false,
    meta: {
      cellClassName: "w-0 px-3 align-top",
      headerClassName: "w-0",
    },
  },
  {
    id: "name",
    accessorFn: getName,
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("exchangeLocations.outletLocationName")} />
    ),
    cell: ({ row }) => getName(row.original) || <PlaceholderValue />,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName:
        "min-w-48 max-w-72 whitespace-normal px-3 align-top font-medium text-foreground",
      headerClassName: cn("min-w-48 max-w-72", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "location",
    accessorFn: exchangeLocation => getAreaLabel(exchangeLocation.location, areaOptions),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("exchangeLocations.location")} />
    ),
    cell: ({ row }) => getAreaLabel(row.original.location, areaOptions) || <PlaceholderValue />,
    sortDescFirst: false,
    sortingFn: "alphanumeric",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 max-w-64 whitespace-normal px-3 align-top",
      headerClassName: cn("min-w-40 max-w-64", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "linkedTraders",
    accessorFn: countLinkedTraders,
    header: ({ column }) => (
      <SortableColumnHeader
        align="end"
        column={column}
        label={t("exchangeLocations.linkedTraders")}
      />
    ),
    cell: ({ row }) => countLinkedTraders(row.original),
    sortingFn: "basic",
    meta: {
      cellClassName: NUMERIC_CLASS,
      headerClassName: cn("max-w-32 text-right", COMPACT_HEADER_CLASS),
    },
  },
  ...sortExchangeLocationTypes(exchangeLocationTypes).flatMap<ExchangeLocationColumnDef>(
    ({ id: typeId, name }) => {
      if (typeId === undefined || typeId === null) {
        return [];
      }

      return [
        {
          id: `type-${typeId}`,
          accessorFn: exchangeLocation => countTradersForType(exchangeLocation, typeId),
          header: ({ column }) => (
            <SortableColumnHeader align="end" column={column} label={name ?? ""} />
          ),
          cell: ({ row }) => countTradersForType(row.original, typeId),
          sortingFn: "basic",
          meta: {
            cellClassName: NUMERIC_CLASS,
            headerClassName: cn("max-w-32 text-right", COMPACT_HEADER_CLASS),
          },
        },
      ];
    }
  ),
  {
    id: "active",
    accessorFn: exchangeLocation => Boolean(exchangeLocation.active),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("exchangeLocations.status")} />
    ),
    cell: ({ row }) => (
      <Badge variant={row.original.active ? "success" : "destructive"}>
        {t(row.original.active ? "common.yes" : "common.no")}
      </Badge>
    ),
    sortingFn: "basic",
    meta: {
      cellClassName: "min-w-24 px-3 align-top",
      headerClassName: cn("min-w-24", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "lastUpdated",
    accessorFn: exchangeLocation => getTimestamp(exchangeLocation.last_update_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.lastUpdated")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.last_update_datetime}
        t={t}
        user={row.original.last_update_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
  {
    id: "created",
    accessorFn: exchangeLocation => getTimestamp(exchangeLocation.creation_datetime),
    header: ({ column }) => (
      <SortableColumnHeader column={column} label={t("configuration.list.column.created")} />
    ),
    cell: ({ row }) => (
      <AuditCell
        dateTime={row.original.creation_datetime}
        t={t}
        user={row.original.creation_user}
      />
    ),
    sortingFn: "basic",
    sortUndefined: "last",
    meta: {
      cellClassName: "min-w-40 px-3 align-top",
      headerClassName: cn("min-w-40", COMPACT_HEADER_CLASS),
    },
  },
];

export const List = ({
  areaOptions = [],
  errorMessage = "",
  exchangeLocations = [],
  exchangeLocationTypes = [],
  isFetching = false,
  onClickDelete,
  onOpenModal,
  onSortingChange,
  searchTerm = "",
  sorting,
}: ListProps) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const columns = useMemo(
    () => createColumns({ areaOptions, exchangeLocationTypes, onClickDelete, onOpenModal, t }),
    [areaOptions, exchangeLocationTypes, onClickDelete, onOpenModal, t]
  );
  const filteredExchangeLocations = useMemo(
    () => filterExchangeLocations(exchangeLocations, areaOptions, searchTerm),
    [areaOptions, exchangeLocations, searchTerm]
  );

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0 ? currentPagination : { ...currentPagination, pageIndex: 0 }
    );
  }, [searchTerm, sorting]);

  useEffect(() => {
    setPagination(currentPagination => {
      const pageCount = Math.max(
        1,
        Math.ceil(filteredExchangeLocations.length / currentPagination.pageSize)
      );
      const nextPageIndex = Math.min(currentPagination.pageIndex, pageCount - 1);

      return nextPageIndex === currentPagination.pageIndex
        ? currentPagination
        : { ...currentPagination, pageIndex: nextPageIndex };
    });
  }, [filteredExchangeLocations.length]);

  const table = useReactTable({
    columns,
    data: filteredExchangeLocations,
    autoResetPageIndex: false,
    enableMultiSort: false,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (exchangeLocation, index) =>
      exchangeLocation.id === undefined || exchangeLocation.id === null
        ? `${index}-${getName(exchangeLocation)}`
        : String(exchangeLocation.id),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange,
    state: { pagination, sorting },
  });

  if (errorMessage) {
    return (
      <Alert variant="destructive">
        <TriangleAlert aria-hidden="true" />
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  const rows = table.getRowModel().rows;
  const columnCount = table.getVisibleLeafColumns().length;

  return (
    <div className="overflow-hidden rounded-md border border-border bg-background">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead
                  key={header.id}
                  className={getColumnMeta(header.column.columnDef)?.headerClassName}
                  aria-sort={getColumnSortAria(header.column)}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isFetching ? (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Loading isVisible small />
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columnCount} className="h-24 text-center text-muted-foreground">
                {t("exchangeLocations.list.empty", { defaultValue: "No exchange locations found" })}
              </TableCell>
            </TableRow>
          ) : null}
          {!isFetching
            ? rows.map(row => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <TableCell
                      key={cell.id}
                      className={getColumnMeta(cell.column.columnDef)?.cellClassName}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : null}
        </TableBody>
      </Table>
      {rows.length > 0 ? <DataTablePagination table={table} /> : null}
    </div>
  );
};
